import React, { ChangeEvent, FC, useEffect, useState } from 'react'
import { IProduct } from '../../interfaces'
import { backURL } from '../../consts'
import './card.css'
import { addToCart } from '../../features/cart/cartSlice'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { Link } from 'react-router-dom'
import { addRemoveCompareProduct } from '../../features/compare/compareSlice'
import useCartProductsCount from '../../hooks/useCartProductsCount'
import { TbCurrencyDram } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai'
import { FaShoppingCart } from 'react-icons/fa'
import noPhoto from '../../imgs/no_product.png'

interface ICardProps {
    product: IProduct
    compareActive: boolean
    inTopSelling?: boolean
}

const Card: FC<ICardProps> = ({ product, compareActive, inTopSelling }) => {
    const { t } = useTranslation()
    const { cart } = useAppSelector((state) => state)
    const [productType, setProductType] = useState('original')
    const { products: compareProducts } = useAppSelector((state) => state.compare)
    const checked = !!compareProducts.find(
        (productItem) => productItem.product_id === product.product_id
    )
    const [productCount_, setProductCount] = useState<{ original: number; copy: number }>({
        original: 0,
        copy: 0,
    })
    const { productCount, btnDisabled, countBtnDisabled, inputDisabled } =
        useCartProductsCount({ cart, productType, product, productCount_, product_id: product.product_id })

    const dispatch = useAppDispatch()
    const { brand, model, detail_number, img, price_original, price_copy } = product

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const inputNumber = +e.target.value
        if (productType === 'original') {
            setProductCount({ ...productCount, original: inputNumber })
        } else {
            setProductCount({ ...productCount, copy: inputNumber })
        }
    }

    const handleAddProduct = () => {
        if (
            (productType === 'original' && productCount.original === 0) ||
            (productType === 'copy' && productCount.copy === 0)
        ) return
        dispatch(addToCart({
            count_original: productType === 'original' ? productCount.original : 0,
            count_copy: productType === 'copy' ? productCount.copy : 0,
            product,
            product_id: product.product_id,
        }))
    }

    const changeProductCount = (action: string) => {
        if (productType === 'original') {
            setProductCount({
                ...productCount,
                original: action === 'increase' ? productCount.original + 1 : productCount.original - 1,
            })
        } else {
            setProductCount({
                ...productCount,
                copy: action === 'increase' ? productCount.copy + 1 : productCount.copy - 1,
            })
        }
    }

    useEffect(() => {
        if (product) {
            const cartProduct = cart.products.find((pr) => pr.product_id === product.product_id)
            if (!product?.count_original) setProductType('copy')
            if (cartProduct) {
                let countOriginal = 0
                let countCopy = 0
                if (cartProduct?.count_original && product.count_original) {
                    countOriginal = product.count_original >= cartProduct.count_original
                        ? cartProduct.count_original : product.count_original
                }
                if (cartProduct?.count_copy && product.count_copy) {
                    countCopy = product.count_copy >= cartProduct.count_copy
                        ? cartProduct.count_copy : product.count_copy
                }
                setProductCount({ original: countOriginal, copy: countCopy })
            } else {
                if (productType === 'original') setProductCount({ ...productCount_, copy: 0 })
                else if (productType === 'copy') setProductCount({ ...productCount_, original: 0 })
            }
        }
    }, [productType, product])

    return (
        <div className="card">
            {/* Meta */}
            <div className="pcard_meta">
                <div><b>{t('product.brand')}:</b> {brand}</div>
                <div><b>{t('product.model')}:</b> {model}</div>
                <div><b>{t('general.detail_num')}:</b> {detail_number}</div>
            </div>

            {/* Image */}
            <div className="product_img">
                <Link to={`/products/${product.category_name}/${product.slug || product.product_id}`}>
                    <img
                        src={img ? `${backURL}/${img}` : noPhoto}
                        alt={`${brand || ''} ${model || ''}`.trim()}
                        onError={(e) => { (e.target as HTMLImageElement).src = noPhoto }}
                    />
                    {!!product.discount && (
                        <span className="product_discount">-{product.discount}%</span>
                    )}
                </Link>
            </div>

            {/* Price + type badges */}
            {(!!product.count_original || !!product.count_copy) && (
                <>
                    <div className="pcard_row">
                        <div className="pcard_price">
                            <b>{productType === 'original' ? price_original : price_copy}</b>
                            <TbCurrencyDram />
                        </div>
                        <div className="pcard_type_toggle">
                            {!!product.count_original && (
                                <label className={`pcard_badge pcard_badge_original ${productType === 'original' ? 'active' : ''}`}>
                                    {t('product.original')}
                                    <input type="radio" hidden checked={productType === 'original'} value="original" onChange={() => setProductType('original')} />
                                </label>
                            )}
                            {!!product.count_copy && (
                                <label className={`pcard_badge pcard_badge_copy ${productType === 'copy' ? 'active' : ''}`}>
                                    {t('product.copy')}
                                    <input type="radio" hidden checked={productType === 'copy'} value="copy" onChange={() => setProductType('copy')} />
                                </label>
                            )}
                        </div>
                    </div>

                    <div className="card_actions">
                        <button
                            disabled={btnDisabled}
                            className={`add_to_cart ${btnDisabled ? 'disabled' : ''}`}
                            onClick={handleAddProduct}
                        >
                            <FaShoppingCart /> {t('general.add_to_cart')}
                        </button>
                        <div className="product_count_container">
                            <button
                                className={countBtnDisabled ? 'card_disabled_item' : ''}
                                disabled={countBtnDisabled}
                                onClick={() => changeProductCount('decrease')}
                            >
                                <AiOutlineMinus />
                            </button>
                            <input
                                className={countBtnDisabled ? 'card_disabled_item' : ''}
                                type="number"
                                disabled={inputDisabled}
                                value={productType === 'original' ? productCount.original : productCount.copy}
                                onChange={handleChange}
                            />
                            <button
                                className={countBtnDisabled ? 'card_disabled_item' : ''}
                                disabled={countBtnDisabled}
                                onClick={() => changeProductCount('increase')}
                            >
                                <AiOutlinePlus />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Sold out */}
            {!product.count_original && !product.count_copy && (
                <Link to={`/products/${product.category_name}/${product.slug || product.product_id}`}>
                    <div className="sold_out_stamp">{t('general.sold_out')}</div>
                </Link>
            )}

            {/* Compare */}
            {compareActive && (
                <div className="compare_box">
                    <input type="checkbox" checked={checked} onChange={() => dispatch(addRemoveCompareProduct({ product }))} />
                    <span>Compare</span>
                </div>
            )}
        </div>
    )
}

export default Card
